import { ChevronRightIcon, Repeat } from 'lucide-react';

import { EXAMPLE_GROUP_ID } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { Card } from '@splitfiesta/ui';

import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { GroupSettingsRowSkeleton } from '@/components/skeleton';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useRecurringRulesQuery } from '@/hooks/queries/useRecurringRules';

interface GroupRecurringSectionProps {
  groupId: string;
}

/** Group settings' row into the group's repeating expenses. */
export function GroupRecurringSection({ groupId }: GroupRecurringSectionProps) {
  const { t } = useTranslation();
  const proEnabled = useFeatureFlag('pro');
  const enabled = proEnabled && groupId !== EXAMPLE_GROUP_ID;
  const { data: rules, isLoading } = useRecurringRulesQuery(groupId, enabled);

  if (!rules) {
    return isLoading ? (
      <GroupSettingsRowSkeleton data-testid="group-settings-recurring-skeleton" />
    ) : null;
  }
  const activeCount = rules.filter(rule => rule.status === 'active').length;

  return (
    <Card className="mb-6">
      <RouteChangeLink
        href={`/groups/${groupId}/recurring`}
        data-testid="group-settings-recurring"
        className="flex w-full min-h-11 flex-wrap items-center gap-x-3 gap-y-1 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
      >
        <span className="flex min-w-[45%] flex-1 items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
          <Repeat aria-hidden="true" className="h-5 w-5 shrink-0" />
          {t('groups:recurring.row')}
        </span>
        <span className="ml-auto flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          {t('groups:recurring.countActive', { count: activeCount })}
          <ChevronRightIcon
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-gray-400"
          />
        </span>
      </RouteChangeLink>
    </Card>
  );
}
